import { Box, Chip, IconButton, Stack, Typography, useTheme, alpha } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import BlockIcon from "@mui/icons-material/Block";
import { useTranslation } from "react-i18next";
import type { RoadmapTask } from "../../api/client";
import TaskCheckbox from "./TaskCheckbox";
import ProductChip from "./ProductChip";

interface TaskCardProps {
  task: RoadmapTask;
  onToggle: (taskId: string, next: boolean) => void;
  isFocus?: boolean;
  weekIndex: number;
}

// Spec §6.5 — radius 12px, 1px border, head grid `checkbox · title · routes · menu`.
// Routes column stacks vertically (max 2 per spec §6.7). Inline detail hides
// on done tasks. Focus task gets coral border + 3px glow; orchestrator
// enforces max one per roadmap.
//
// Blocked state (spec §10): 4px red left-border + a "Blocked" chip, shown when
// task.blocked is set by the backend phase-derivation logic.
export default function TaskCard({
  task,
  onToggle,
  isFocus = false,
  weekIndex,
}: TaskCardProps) {
  const theme = useTheme();
  const { t } = useTranslation("roadmap");
  const done = task.done;
  const blocked = task.blocked;
  const routes = task.routes.slice(0, 2); // spec §6.7 max 2 chips

  return (
    <Box
      component="article"
      data-task-id={task.id}
      data-week={weekIndex}
      data-blocked={blocked || undefined}
      sx={{
        borderRadius: "12px",
        border: "1px solid",
        borderColor: isFocus
          ? "secondary.main"
          : alpha(theme.palette.text.primary, 0.12),
        // Blocked: 4px red left rail (spec §10). Padding compensates the extra
        // 3px so inner content doesn't shift relative to a normal card.
        ...(blocked && {
          borderLeftWidth: "4px",
          borderLeftColor: theme.palette.error.main,
          pl: { xs: "calc(16px - 3px)", sm: "calc(20px - 3px)" },
        }),
        bgcolor: done
          ? alpha(theme.palette.text.primary, 0.04)
          : "background.paper",
        boxShadow: isFocus
          ? `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.18)}`
          : "none",
        p: { xs: 2, sm: 2.5 },
        transition: theme.transitions.create(
          ["border-color", "box-shadow", "background-color"],
          { duration: theme.transitions.duration.shortest },
        ),
        "&:hover": {
          borderColor: isFocus
            ? "secondary.main"
            : alpha(theme.palette.text.primary, 0.35),
        },
      }}
    >
      {/* Head: checkbox · title (+ meta) · routes · menu
          Layout uses named grid areas so placement is unambiguous at both
          breakpoints. At ≤900px (md) routes drop below title per spec §9. */}
      <Box
        sx={{
          display: "grid",
          alignItems: "start",
          columnGap: 2,
          rowGap: 1,
          gridTemplateColumns: {
            xs: "20px 1fr 28px",
            md: "20px 1fr auto 28px",
          },
          gridTemplateAreas: {
            xs: `
              "checkbox title    menu"
              ".        routes   ."
            `,
            md: `"checkbox title routes menu"`,
          },
        }}
      >
        <Box sx={{ gridArea: "checkbox", pt: "2px" }}>
          <TaskCheckbox
            checked={done}
            onChange={(next) => onToggle(task.id, next)}
            ariaLabel={t("TaskCard.markTaskAriaLabel", {
              title: task.title,
              state: done ? t("TaskCard.stateIncomplete") : t("TaskCard.stateComplete"),
            })}
            disabled={task.blocked}
          />
        </Box>

        <Box sx={{ gridArea: "title", minWidth: 0 }}>
          <Typography
            variant="body1"
            component="h3"
            sx={{
              fontWeight: 500,
              lineHeight: 1.4,
              color: done ? "text.secondary" : "text.primary",
              textDecoration: done ? "line-through" : "none",
              textDecorationColor: alpha(theme.palette.text.secondary, 0.5),
            }}
          >
            {task.title}
          </Typography>
          {!done && (task.estimate_hours > 0 || task.due_day) && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ mt: 0.5 }}
              divider={
                <Typography component="span" variant="caption" color="text.secondary">·</Typography>
              }
            >
              {task.estimate_hours > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {task.estimate_hours}h
                </Typography>
              )}
              {task.due_day && (
                <Typography variant="caption" color="text.secondary">
                  {t("TaskCard.dueLabel", { day: task.due_day })}
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        {/* Routes — vertical stack per spec §6.5. At xs these wrap to row 2
            below title; at md they sit in the third column to the right. The
            Blocked chip (spec §10) leads the column and renders even with no
            routes. */}
        {(blocked || routes.length > 0) && (
          <Stack
            spacing={0.75}
            direction={{ xs: "row", md: "column" }}
            flexWrap="wrap"
            useFlexGap
            sx={{
              gridArea: "routes",
              alignItems: { xs: "flex-start", md: "flex-end" },
              minWidth: 0,
            }}
          >
            {blocked && (
              <Chip
                size="small"
                icon={<BlockIcon sx={{ fontSize: 15 }} />}
                label={t("TaskCard.blockedChipLabel")}
                sx={{
                  height: 24,
                  fontSize: 12,
                  fontWeight: 500,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  "& .MuiChip-icon": { color: theme.palette.error.main, ml: "6px" },
                }}
              />
            )}
            {routes.map((route, idx) => (
              <ProductChip
                key={`${route.product_slug}-${idx}`}
                productSlug={route.product_slug}
                label={route.label}
                href={route.href}
              />
            ))}
          </Stack>
        )}

        <IconButton
          size="small"
          aria-label={t("TaskCard.taskOptionsAriaLabel", { title: task.title })}
          disabled
          sx={{
            gridArea: "menu",
            width: 28,
            height: 28,
            color: "text.secondary",
            opacity: 0.4,
            // TODO(v1.1): wire menu — Reschedule / Edit / Mark as blocked options.
          }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Inline detail — hidden when done (spec §6.5) */}
      {!done && (task.summary || task.checklist.length > 0) && (
        <Box
          sx={{
            pl: { xs: 0, sm: 4.5 }, // align with title under head row
            mt: 1.5,
          }}
        >
          {task.summary && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6, mb: task.checklist.length > 0 ? 1 : 0 }}
            >
              {task.summary}
            </Typography>
          )}
          {task.checklist.length > 0 && (
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                "& li": {
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "text.secondary",
                  mb: 0.25,
                },
              }}
            >
              {task.checklist.slice(0, 3).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
